import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('paniclock_token'));

  const api = axios.create({
    baseURL: API,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth header interceptor
  api.interceptors.request.use((config) => {
    const storedToken = localStorage.getItem('paniclock_token');
    if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
  });

  // Handle 401 responses
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('paniclock_token');
        setToken(null);
        setUser(null);
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    const storedToken = localStorage.getItem('paniclock_token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('paniclock_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password, totpCode = null) => {
    const response = await api.post('/auth/login', {
      email,
      password,
      totp_code: totpCode,
    });

    if (response.data.mfa_required) {
      return { mfaRequired: true, tempToken: response.data.temp_token };
    }

    localStorage.setItem('paniclock_token', response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return { success: true, user: response.data.user };
  };

  const verifyMfa = async (tempToken, totpCode) => {
    const response = await api.post('/auth/verify-mfa', null, {
      params: { temp_token: tempToken, totp_code: totpCode },
    });

    localStorage.setItem('paniclock_token', response.data.access_token);
    setToken(response.data.access_token);
    await fetchUser();
    return { success: true };
  };

  const register = async (email, password, fullName) => {
    const response = await api.post('/auth/register', {
      email,
      password,
      full_name: fullName,
    });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('paniclock_token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const value = {
    user,
    token,
    loading,
    login,
    verifyMfa,
    register,
    logout,
    refreshUser,
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
